import { useEffect } from 'react';
import type { KeycloakInstance } from 'keycloak-js';
import { SESSION_KEEPALIVE_INTERVAL_MS } from './webSession';

type KeepAliveClient = Pick<KeycloakInstance, 'authenticated' | 'tokenParsed' | 'updateToken'>;

/** Milliseconds since the current access token was issued. */
const tokenAgeMs = (keycloak: KeepAliveClient, nowMs: number): number => {
  const issuedAt = keycloak.tokenParsed?.iat;
  return typeof issuedAt === 'number' ? nowMs - issuedAt * 1000 : Number.POSITIVE_INFINITY;
};

/**
 * Keep the Keycloak session alive while the app is open.
 *
 * The access token lives two hours, but the realm forgets a session that
 * nothing has refreshed for 30 minutes. Without this, a user who works for
 * 35 minutes without a refresh is signed out at the next token expiry or
 * reload. A forced refresh every 20 minutes while the tab is visible, and one
 * on return to a tab whose token is that old, keeps the session current.
 * A failed refresh is handled by keycloak-js and the provider as before.
 */
export function useKeycloakSessionKeepAlive(
  keycloak: KeepAliveClient | null | undefined,
  enabled = true,
  intervalMs = SESSION_KEEPALIVE_INTERVAL_MS,
): void {
  useEffect(() => {
    if (!enabled || !keycloak || typeof document === 'undefined') return undefined;

    const refreshIfDue = (force: boolean) => {
      if (!keycloak.authenticated || document.visibilityState !== 'visible') return;
      if (!force && tokenAgeMs(keycloak, Date.now()) < intervalMs) return;
      // -1 forces a refresh whatever the access token's remaining life.
      void keycloak.updateToken(-1).catch(() => undefined);
    };

    const timer = window.setInterval(() => refreshIfDue(true), intervalMs);
    const onVisible = () => refreshIfDue(false);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [keycloak, enabled, intervalMs]);
}
